import React from "react";
import { Eye, Info } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

// Requirement "kinds" drive the colour of the badge.
const KIND = {
  everyone: { label: "Everyone", bg: "#E3F1EC", color: "#0B5E4E" },
  staff: { label: "Staff", bg: "#FBEAD7", color: "#8a5a1a" },
  cap: { label: "Capability", bg: "#E7EEF1", color: "#2B5563" },
  admin: { label: "Admin only", bg: "#FBE4E4", color: "#B4433B" },
};

// Read-only map mirroring the sidebar's access rules.
// "Staff" = Admin, Pastor or Leader. Admin & Pastor always have every capability.
const SECTIONS = [
  {
    title: "Personal (all members)",
    note: "Available to everyone who is signed in.",
    items: [
      { label: "My Portal", req: "Everyone", kind: "everyone" },
      { label: "My Courses", req: "Everyone", kind: "everyone" },
      { label: "Church QR Codes", req: "Everyone", kind: "everyone" },
      { label: "Calendar", req: "Everyone", kind: "everyone" },
      { label: "Gallery", req: "Everyone", kind: "everyone" },
      { label: "Sermons", req: "Everyone", kind: "everyone" },
      { label: "Giving", req: "Everyone", kind: "everyone" },
    ],
  },
  {
    title: "Church Dashboard",
    items: [
      { label: "Dashboard", req: "Staff (Admin, Pastor or Leader)", kind: "staff" },
      { label: "My Team", req: "Team Leaders", kind: "staff" },
    ],
  },
  {
    title: "Membership Management",
    items: [
      { label: "Members", req: "Manage members", kind: "cap" },
      { label: "Pastoral Care", req: "Pastoral care", kind: "cap" },
      { label: "Follow-up", req: "Pastoral care", kind: "cap" },
      { label: "Evangelism", req: "Evangelism / Evangelism follow-up / Manage members", kind: "cap" },
      { label: "Visitor Care", req: "Visitor care / Manage members", kind: "cap" },
      { label: "Teams", req: "Staff (Admin, Pastor or Leader)", kind: "staff" },
      { label: "Prayer Requests", req: "View prayer requests", kind: "cap" },
    ],
  },
  {
    title: "Course Management",
    items: [
      { label: "Courses", req: "Staff or Manage courses / content", kind: "cap" },
      { label: "Learner Replies", req: "Manage courses / content", kind: "cap" },
    ],
  },
  {
    title: "General Church Management",
    items: [
      { label: "Attendance", req: "Staff (Admin, Pastor or Leader)", kind: "staff" },
      { label: "Conversions", req: "Staff (Admin, Pastor or Leader)", kind: "staff" },
      { label: "Calendar", req: "Everyone", kind: "everyone" },
      { label: "Documents", req: "Any staff capability", kind: "cap" },
      { label: "Gallery", req: "Everyone", kind: "everyone" },
      { label: "Sermons", req: "Everyone", kind: "everyone" },
      { label: "Giving", req: "Everyone", kind: "everyone" },
      { label: "Messaging", req: "Staff (Admin, Pastor or Leader)", kind: "staff" },
      { label: "Church QR Codes", req: "Everyone except plain members", kind: "cap" },
    ],
  },
  {
    title: "Systems Admin",
    note: "Sensitive settings — mostly restricted to Administrators.",
    items: [
      { label: "Users & Access", req: "Admin only", kind: "admin" },
      { label: "Roles & Permissions", req: "Admin only", kind: "admin" },
      { label: "Alerts", req: "Admin only", kind: "admin" },
      { label: "Calling Audit", req: "Admin only", kind: "admin" },
      { label: "Access Overview", req: "Admin only", kind: "admin" },
      { label: "Message Log", req: "Any staff capability", kind: "cap" },
      { label: "Deletion Log", req: "Admin only", kind: "admin" },
      { label: "Portal Settings", req: "Admin only", kind: "admin" },
      { label: "Message Templates", req: "Admin or Pastor", kind: "admin" },
    ],
  },
];

export default function AccessOverview() {
  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="access-overview-page">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Eye className="h-7 w-7" style={{ color: "var(--sj-primary)" }} /> Access Overview
        </h1>
        <p style={{ color: "var(--sj-text-2)" }}>Every menu item and what a person needs to see it. This page is read-only.</p>
      </div>

      <div className="rounded-2xl p-4 flex items-start gap-3" style={{ background: "#E7EEF1" }} data-testid="access-overview-readonly-note">
        <Info className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "#2B5563" }} />
        <p className="text-sm" style={{ color: "#2B5563" }}>
          This is a <b>read-only reference</b>. To change who can do what, use <b>Roles &amp; Permissions</b> (per role and per team)
          and <b>Teams</b> (to enable calling and assign roles). "Staff" means Admin, Pastor or Leader; Admin and Pastor always have every capability.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="access-legend">
        {Object.entries(KIND).map(([k, v]) => (
          <span key={k} className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: v.bg, color: v.color }}>{v.label}</span>
        ))}
      </div>

      <div className="space-y-5">
        {SECTIONS.map((s) => (
          <div key={s.title} className={`${CARD} p-5 sm:p-6`} data-testid={`access-section-${s.title.replace(/\s+/g, "-").toLowerCase()}`}>
            <h2 className="font-head text-lg font-semibold">{s.title}</h2>
            {s.note && <p className="text-sm mt-0.5 mb-3" style={{ color: "var(--sj-text-3)" }}>{s.note}</p>}
            <div className="mt-3 divide-y" style={{ borderColor: "var(--sj-border)" }}>
              {s.items.map((it) => {
                const k = KIND[it.kind] || KIND.cap;
                return (
                  <div key={it.label} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                    <span className="font-medium" style={{ color: "var(--sj-text)" }}>{it.label}</span>
                    <span className="inline-flex items-center gap-2">
                      <span className="text-sm" style={{ color: "var(--sj-text-2)" }}>{it.req}</span>
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full" style={{ background: k.bg, color: k.color }}>{k.label}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
